import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const GoogleIntegration = () => {
  const [settings, setSettings] = useState({
    google_analytics_id: '',
    google_ads_id: '',
    google_ads_conversion_label: '',
    google_tag_manager_id: '',
    google_analytics_enabled: false,
    google_ads_enabled: false
  });

  const [showIds, setShowIds] = useState({
    analytics: false,
    ads: false,
    tagManager: false
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    try {
      const saved = localStorage.getItem('google-integration-settings');
      if (saved) {
        setSettings(JSON.parse(saved));
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoading(false);
    }
  };

  const saveSettings = () => {
    try {
      localStorage.setItem('google-integration-settings', JSON.stringify(settings));
      toast.success('Google-Einstellungen gespeichert!');
      
      // Reload page to activate tracking codes
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#E8E0D5' }}>
        <p className="text-center">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Google Integration</h1>
            <p className="text-sm text-gray-600 mt-1">Verbinden Sie Ihren Shop mit Google Analytics & Ads</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Google Analytics Card */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl">📊</span>
                    Google Analytics
                  </CardTitle>
                  <CardDescription className="mt-2">
                    Verfolgen Sie Besucher, Seitenaufrufe und Nutzerverhalten auf Ihrer Website
                  </CardDescription>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, google_analytics_enabled: !settings.google_analytics_enabled })}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    settings.google_analytics_enabled
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                  }`}
                >
                  {settings.google_analytics_enabled ? 'Aktiv' : 'Inaktiv'}
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="analytics-id" className="text-base font-semibold">
                  Google Analytics Mess-ID
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Ihre Analytics-ID beginnt mit "G-" oder "UA-"
                </p>
                <div className="flex gap-2">
                  <Input
                    id="analytics-id"
                    type={showIds.analytics ? 'text' : 'password'}
                    value={settings.google_analytics_id}
                    onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX oder UA-XXXXXXXXX-X"
                    disabled={!settings.google_analytics_enabled}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowIds({ ...showIds, analytics: !showIds.analytics })}
                  >
                    {showIds.analytics ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Wie finde ich meine Analytics-ID?</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Gehen Sie zu <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline">analytics.google.com</a></li>
                  <li>Melden Sie sich an und wählen Sie Ihre Property</li>
                  <li>Gehen Sie zu "Verwaltung" → "Datenstreams"</li>
                  <li>Klicken Sie auf Ihren Web-Stream</li>
                  <li>Kopieren Sie die "Mess-ID" (beginnt mit G-)</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Google Ads Card */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl">🎯</span>
                    Google Ads (Werbung)
                  </CardTitle>
                  <CardDescription className="mt-2">
                    Verfolgen Sie Conversions und optimieren Sie Ihre Google-Werbekampagnen
                  </CardDescription>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, google_ads_enabled: !settings.google_ads_enabled })}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    settings.google_ads_enabled
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                  }`}
                >
                  {settings.google_ads_enabled ? 'Aktiv' : 'Inaktiv'}
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="ads-id" className="text-base font-semibold">
                  Google Ads Conversion-ID
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Ihre Conversion-ID beginnt mit "AW-"
                </p>
                <div className="flex gap-2">
                  <Input
                    id="ads-id"
                    type={showIds.ads ? 'text' : 'password'}
                    value={settings.google_ads_id}
                    onChange={(e) => setSettings({ ...settings, google_ads_id: e.target.value })}
                    placeholder="AW-XXXXXXXXXX"
                    disabled={!settings.google_ads_enabled}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowIds({ ...showIds, ads: !showIds.ads })}
                  >
                    {showIds.ads ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="conversion-label" className="text-base font-semibold">
                  Conversion-Label (optional)
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Für erweiterte Conversion-Verfolgung
                </p>
                <Input
                  id="conversion-label"
                  value={settings.google_ads_conversion_label}
                  onChange={(e) => setSettings({ ...settings, google_ads_conversion_label: e.target.value })}
                  placeholder="z.B. purchase"
                  disabled={!settings.google_ads_enabled}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Wie finde ich meine Google Ads IDs?</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Gehen Sie zu <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="underline">ads.google.com</a></li>
                  <li>Klicken Sie auf "Tools und Einstellungen" → "Conversions"</li>
                  <li>Wählen Sie eine Conversion-Aktion aus</li>
                  <li>Unter "Tag-Einrichtung" finden Sie die Conversion-ID</li>
                  <li>Format: AW-XXXXXXXXXX/LABEL</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Google Tag Manager Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">🏷️</span>
                Google Tag Manager (Erweitert)
              </CardTitle>
              <CardDescription>
                Verwalten Sie alle Tracking-Tags zentral (optional, für Fortgeschrittene)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="gtm-id" className="text-base font-semibold">
                  Google Tag Manager ID
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Ihre GTM-ID beginnt mit "GTM-"
                </p>
                <div className="flex gap-2">
                  <Input
                    id="gtm-id"
                    type={showIds.tagManager ? 'text' : 'password'}
                    value={settings.google_tag_manager_id}
                    onChange={(e) => setSettings({ ...settings, google_tag_manager_id: e.target.value })}
                    placeholder="GTM-XXXXXXX"
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowIds({ ...showIds, tagManager: !showIds.tagManager })}
                  >
                    {showIds.tagManager ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Hinweis:</strong> Wenn Sie Tag Manager verwenden, müssen Sie Analytics und Ads 
                  dort konfigurieren. Die obigen IDs werden dann nicht mehr benötigt.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Card>
            <CardContent className="pt-6">
              <Button
                onClick={saveSettings}
                className="w-full bg-[#C4A084] hover:bg-[#B89968]"
                size="lg"
              >
                <Save className="h-4 w-4 mr-2" />
                Einstellungen speichern & aktivieren
              </Button>
              <p className="text-xs text-center text-gray-600 mt-3">
                Nach dem Speichern wird die Seite neu geladen, um die Tracking-Codes zu aktivieren
              </p>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="border-2 border-blue-300 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="text-blue-600 text-2xl">💡</div>
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">Was bringt mir Google Integration?</h3>
                  <ul className="text-sm text-blue-800 space-y-2">
                    <li>
                      <strong>Google Analytics:</strong> Sehen Sie, wie viele Besucher Sie haben, woher sie kommen 
                      und welche Seiten am beliebtesten sind
                    </li>
                    <li>
                      <strong>Google Ads:</strong> Schalten Sie Werbeanzeigen bei Google und messen Sie, 
                      wie viele Verkäufe durch Ihre Anzeigen entstehen
                    </li>
                    <li>
                      <strong>Optimierung:</strong> Nutzen Sie die Daten, um Ihre Website und Werbung zu verbessern
                    </li>
                    <li>
                      <strong>Remarketing:</strong> Zeigen Sie Anzeigen an Besucher, die Ihren Shop bereits besucht haben
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy Notice */}
          <Card className="border-2 border-orange-300 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="text-orange-600 text-2xl">⚠️</div>
                <div>
                  <h3 className="font-semibold text-orange-900 mb-2">Wichtig: Datenschutz</h3>
                  <p className="text-sm text-orange-800">
                    Wenn Sie Google Tracking verwenden, müssen Sie Ihre Kunden darüber informieren. 
                    Stellen Sie sicher, dass Ihre <strong>Datenschutzerklärung</strong> aktualisiert ist 
                    und erwähnen Sie die Verwendung von Google Analytics/Ads. Der Cookie-Banner auf Ihrer 
                    Website erfüllt bereits die grundlegenden Anforderungen.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GoogleIntegration;
